import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterTooltip from './FilterTooltip';
import Iconify from './Iconify';
import OverflowBox from './OverflowBox';
import debounce from 'src/utils/debounce';
import IconButton from 'src/components/IconButton';

const StyledSearchInput = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#4B4B4B',
    borderRadius: '8px',
    padding: '14px 16px',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-root input': {
    padding: 0,
  },
}));

const ListActionBar = ({
  onSearch = null,
  filters = {},
  onFilterChange = null,
  filterOptions = [],
  searchPlaceholder = '...',
  defaultSearch = null,
  onResetSearch = null,
  filterChips,
  limitChips,
  leadingChips = null,
  filterButtonCount,
  actionArea,
  actionAreaContainerProps,
  contentArea,
  contentAreaContainerProps,
  isMobile = false,
}) => {
  const [searchValue, setSearchValue] = useState(defaultSearch || '');

  useEffect(() => {
    setSearchValue(defaultSearch || '');
  }, [defaultSearch]);

  const debouncedSearch = useMemo(
    () => (onSearch ? debounce(value => onSearch(value), 300) : null),
    [onSearch],
  );

  const handleSearchChange = useCallback(
    event => {
      const nextValue = event.target.value;
      setSearchValue(nextValue);
      if (debouncedSearch) {
        debouncedSearch(nextValue);
      }
    },
    [debouncedSearch],
  );

  const handleResetSearch = useCallback(() => {
    setSearchValue('');
    if (onResetSearch) {
      onResetSearch();
      return;
    }

    if (onSearch) {
      onSearch('');
    }
  }, [onResetSearch, onSearch]);

  return (
    <Stack
      className="list-action-bar"
      direction={'column'}
      alignItems="stretch"
      justifyContent="flex-start"
      py={0.5}
      gap={1}
      sx={{ width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
      <Stack
        className="action-buttons-row"
        direction={'row'}
        justifyContent={'space-between'}
        alignItems="center"
        gap={1}
        sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <Stack
          flexShrink={1}
          className="action-buttons"
          direction={'row'}
          justifyContent={'flex-start'}
          alignItems="center"
          gap={1}
          sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
          {...actionAreaContainerProps}>
          {actionArea}
        </Stack>
        <Stack
          className="filter-actions"
          direction={'row'}
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
          sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {(filterChips || leadingChips) && (
            <OverflowBox
              rowHeight={32}
              boxProps={{
                gap: 1,
                justifyContent: 'flex-end',
              }}>
              {leadingChips}
              {filterChips && (
                <FilterObjectToChips
                  filter={filters}
                  filterOptions={filterOptions}
                  onFilterChange={onFilterChange}
                  limitChips={limitChips}
                />
              )}
            </OverflowBox>
          )}
          {filterButtonCount != null && (
            <Chip
              label={`${filterButtonCount.toLocaleString()} result${
                filterButtonCount === 1 ? '' : 's'
              }`}
              sx={{
                backgroundColor: '#3F3F3F',
                color: '#DDDDDD',
                borderRadius: '8px',
                fontWeight: 500,
                height: 32,
              }}
            />
          )}
          {filterOptions && filterOptions.length > 0 && (
            <FilterTooltip
              filters={filters}
              onFilterChange={onFilterChange}
              filterOptions={filterOptions}
              buttonLabel="Filter"
            />
          )}
        </Stack>
      </Stack>
      {contentArea ? (
        <Stack
          className="content-area"
          direction="row"
          alignItems="stretch"
          justifyContent="flex-start"
          sx={{ width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
          {...contentAreaContainerProps}>
          {contentArea}
        </Stack>
      ) : null}
      {onSearch && (
        <StyledSearchInput
          variant="outlined"
          type="text"
          placeholder={searchPlaceholder || 'Search...'}
          value={searchValue}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="material-symbols:search" color="#6B7280" width={20} height={20} />
              </InputAdornment>
            ),
            ...(searchValue?.length
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleResetSearch}>
                        <Iconify icon="ic:outline-close" color="#DDDDDD" width={20} height={20} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : {}),
          }}
          onChange={handleSearchChange}
        />
      )}
    </Stack>
  );
};

ListActionBar.propTypes = {
  onSearch: PropTypes.func,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'number', 'radio', 'checkbox', 'switch', 'date']).isRequired,
      label: PropTypes.string.isRequired,
      dataType: PropTypes.oneOf(['string', 'number', 'boolean']), // optional configuration for filter params to leverage
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
            .isRequired,
          label: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  searchPlaceholder: PropTypes.string,
  defaultSearch: PropTypes.string,
  onResetSearch: PropTypes.func,
  filterChips: PropTypes.bool,
  limitChips: PropTypes.number,
  leadingChips: PropTypes.node,
  filterButtonCount: PropTypes.number,
  actionArea: PropTypes.node,
  actionAreaContainerProps: PropTypes.object,
  contentArea: PropTypes.node,
  contentAreaContainerProps: PropTypes.object,
  isMobile: PropTypes.bool,
};

export default ListActionBar;

// A filter a page has not set at all is as unset as one holding an empty value, so pages are free to
// leave the key out entirely rather than carrying an empty string for it.
const isUnset = value => value == null || value === '';

const FilterObjectToChips = ({ filter, filterOptions, onFilterChange, limitChips = Infinity }) => {
  var tagCount = 0;
  const ret = (
    <>
      {filterOptions.map(filterItem => {
        switch (filterItem.type) {
          case 'radio':
            // Only show chip if there's a valid selection
            if (
              filter[filterItem.key] &&
              filterItem.options.find(x => x.value === filter[filterItem.key])
            ) {
              tagCount++;
              return (
                <Chip
                  className="filter-chip"
                  key={filterItem.key}
                  label={`${filterItem.label}: ${
                    filterItem.options.find(x => x.value === filter[filterItem.key]).label
                  }`}
                  sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                />
              );
            }
            return <Fragment key={filterItem.key}></Fragment>;
          case 'checkbox':
            if (!filter[filterItem.key] || filter[filterItem.key].length === 0) {
              return <Fragment key={filterItem.key}></Fragment>;
            }
            tagCount++;
            const selectedLabels = filter[filterItem.key]
              .map(value => filterItem.options.find(x => x.value === value)?.label ?? String(value))
              .join(' / ');
            return (
              <Chip
                className="filter-chip"
                key={filterItem.key}
                label={`${filterItem.label}: ${selectedLabels}`}
                sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                onDelete={() => {
                  onFilterChange({
                    ...filter,
                    [filterItem.key]: [],
                  });
                }}></Chip>
            );
          case 'switch':
            if (!filter[filterItem.key]) {
              return <Fragment key={filterItem.key}></Fragment>;
            }
            tagCount++;
            return (
              <Chip
                className="filter-chip"
                key={filterItem.key + filter[filterItem.key]}
                label={`${filterItem.label}: ${
                  filterItem.options.find(x => x.value === filter[filterItem.key])?.label ?? ''
                }`}
                sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                onDelete={() => {
                  onFilterChange({
                    ...filter,
                    [filterItem.key]: false,
                  });
                }}></Chip>
            );
          case 'select':
            if (isUnset(filter[filterItem.key])) {
              return <Fragment key={filterItem.key}></Fragment>;
            }
            tagCount++;
            const selectedOption =
              filterItem.options.find(x => x.value === filter[filterItem.key]) || null;
            const selectedLabel = selectedOption
              ? selectedOption.label
              : String(filter[filterItem.key]);
            return (
              <Chip
                className="filter-chip"
                key={filterItem.key + filter[filterItem.key]}
                label={`${filterItem.label}: ${selectedLabel}`}
                sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                onDelete={() => {
                  onFilterChange({
                    ...filter,
                    [filterItem.key]: '',
                  });
                }}></Chip>
            );
          case 'number':
            if (isUnset(filter[filterItem.key])) {
              return <Fragment key={filterItem.key}></Fragment>;
            }
            tagCount++;
            return (
              <Chip
                className="filter-chip"
                key={filterItem.key + filter[filterItem.key]}
                label={`${filterItem.label}: ${filter[filterItem.key]}`}
                sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                onDelete={() => {
                  onFilterChange({
                    ...filter,
                    [filterItem.key]: '',
                  });
                }}></Chip>
            );
          case 'date':
            if (!filter[filterItem.key]) {
              return <Fragment key={filterItem.key}></Fragment>;
            }

            tagCount++;
            return (
              <Chip
                className="filter-chip"
                key={filterItem.key}
                label={`${filterItem.label} ${filter[filterItem.key].toLocaleDateString()}`}
                sx={{ display: tagCount > limitChips ? 'none' : 'flex' }}
                onDelete={() => {
                  onFilterChange({
                    ...filter,
                    [filterItem.key]: null,
                  });
                }}
              />
            );
          default:
            return <Fragment key={filterItem.key}></Fragment>;
        }
      })}
      {tagCount > limitChips && (
        <Typography alignSelf={'center'}>{'+' + (tagCount - limitChips)}</Typography>
      )}
    </>
  );
  return ret;
};
